import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import './CoachifyPages.css';
import CoachifyLogo from '../../assets/logos/CoachifyHoriLogo.png';

const Pricing = () => {
  const pricingPlans = [
    {
      name: 'Starter',
      tagline: 'Perfect for boutique studios',
      price: '$299',
      period: 'per month',
      features: [
        'Up to 50 active clients',
        '2 coach accounts',
        'Nutrition planning & tracking',
        'Workout program builder',
        'Client progress analytics',
        'Mobile-responsive interface',
        'Email support',
        'White-label branding',
      ],
      highlighted: false
    },
    {
      name: 'Professional',
      tagline: 'For growing gyms',
      price: '$599',
      period: 'per month',
      features: [
        'Up to 150 active clients',
        '5 coach accounts',
        'Everything in Starter, plus:',
        'Advanced analytics dashboard',
        'Client invitation system',
        'Integrated messaging',
        'Calendar & scheduling',
        'Template library',
        'Priority support',
        'Custom domain setup'
      ],
      highlighted: true
    },
    {
      name: 'Enterprise',
      tagline: 'For multi-location operations',
      price: 'Custom',
      period: 'pricing',
      features: [
        'Unlimited clients',
        'Unlimited coach accounts',
        'Everything in Professional, plus:',
        'Multi-location management',
        'Advanced reporting & exports',
        'API access',
        'Dedicated account manager',
        'Custom integrations',
        'White-glove onboarding',
        'SLA guarantee',
        '24/7 phone support'
      ],
      highlighted: false
    }
  ];

  return (
    <div className="coachify-page">
      {/* Header Navigation */}
      <header className="coachify-header">
        <div className="header-logo">
          <Link to="/" className="logo-link">
            <img src={CoachifyLogo} alt="Coachify" className="header-logo-img" />
          </Link>
        </div>
        <nav className="coachify-nav">
          <Link to="/about" className="nav-link">About</Link>
          <Link to="/features" className="nav-link">Features</Link>
          <Link to="/pricing" className="nav-link active">Pricing</Link>
          <Link to="/contact" className="nav-link">Contact</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-container">
          <h1 className="page-title">Simple, Transparent Pricing</h1>
          <p className="page-subtitle">
            Choose the plan that fits your gym's size and ambition. No hidden fees.
            Cancel anytime.
          </p>
        </div>
      </section>

      {/* Pricing Content */}
      <section className="page-content">
        <div className="page-container">
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}
              >
                {plan.highlighted && <div className="popular-badge">Most Popular</div>}
                <div className="pricing-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-tagline">{plan.tagline}</p>
                  <div className="plan-price">
                    <span className="price">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                </div>
                <div className="pricing-features">
                  <ul className="features-list">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="feature-item">
                        <FiCheck className="check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pricing-footer">
                  <Link
                    to="/contact"
                    className={`pricing-button ${plan.highlighted ? 'primary' : 'secondary'}`}
                  >
                    {plan.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="content-section">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3 className="faq-question">What's included in white-label branding?</h3>
                <p className="faq-answer">
                  All plans include full white-label capabilities. Your gym's logo, colors,
                  and branding replace Coachify branding throughout the platform. The
                  Professional and Enterprise plans include custom domain setup (e.g.,
                  coaching.yourgym.com).
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-question">Can I upgrade or downgrade my plan?</h3>
                <p className="faq-answer">
                  Yes! You can change your plan at any time. Upgrades take effect immediately.
                  Downgrades take effect at the start of your next billing cycle.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-question">Is there a setup fee?</h3>
                <p className="faq-answer">
                  No setup fees for Starter or Professional plans. Enterprise plans include
                  white-glove onboarding with a dedicated implementation specialist at no
                  additional cost.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-question">What payment methods do you accept?</h3>
                <p className="faq-answer">
                  We accept all major credit cards (Visa, Mastercard, Amex) and ACH transfers
                  for annual plans. Enterprise plans can be invoiced quarterly or annually.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-question">Do you offer discounts for annual billing?</h3>
                <p className="faq-answer">
                  Yes! Save 20% when you choose annual billing on any plan. Contact our sales
                  team for multi-year discount options.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-question">Can I try before I buy?</h3>
                <p className="faq-answer">
                  Absolutely! We offer a 14-day free trial on all plans with no credit card
                  required. Schedule a demo to see the platform in action with your team.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="content-section cta-section">
            <h2 className="section-title">Ready to Get Started?</h2>
            <p className="section-text">
              Join leading gyms who are transforming their coaching delivery with Coachify.
            </p>
            <Link to="/contact" className="cta-button">Start Your Free Trial</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="coachify-footer">
        <p>&copy; 2025 Coachify. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Pricing;
